import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ContactUs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTopicRequest = searchParams.get("topic_request") === "true";
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        ...form,
        message: isTopicRequest ? `[Topic Request] ${form.message}` : form.message,
        source: isTopicRequest ? "topic_request" : "app",
      });
      setSent(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-16" data-testid="contact-us-page">
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8" data-testid="contact-us-logo-link">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_student-genius-26/artifacts/kbshcq5q_Branding-02.png"
            alt="SmartGuru Logo Icon"
            className="w-9 h-9 object-contain"
          />
          <p className="font-heading font-extrabold text-lg bg-gradient-to-r from-[#9A3DB2] via-[#4F4CB9] to-[#E73F9A] bg-clip-text text-transparent">SmartGuru.io</p>
        </Link>
        <div className="bg-card border border-border rounded-3xl shadow-lg p-7">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <Mail className="text-primary" size={22} />
          </div>
          <h1 className="font-heading font-extrabold text-2xl mb-1" data-testid="contact-us-heading">{isTopicRequest ? "Request a Topic" : "Contact Us"}</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {isTopicRequest
              ? "Couldn't find a chapter or topic you were looking for? Tell us which one and we'll add it to the curriculum."
              : "Questions, feedback or a bug to report? Send us a message and our team will get back to you."}
          </p>
          {sent ? (
            <div className="text-center py-6" data-testid="contact-us-success">
              <p className="font-heading font-bold text-lg mb-2">Thank you!</p>
              <p className="text-sm text-muted-foreground mb-5">Your message has been received.</p>
              <button onClick={() => navigate("/")} data-testid="contact-us-back-home" className="text-primary font-semibold text-sm underline">
                Back to Home
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-us-form">
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Name</label>
                <input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-1.5 h-11 rounded-xl border border-border bg-background px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Email</label>
                <input
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1.5 h-11 rounded-xl border border-border bg-background px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Message</label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="w-full mt-1.5 rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  placeholder={isTopicRequest ? "e.g. Please add Class 7 Science - Winds, Storms and Cyclones" : "How can we help?"}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60"
              >
                <Send size={16} /> {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
